import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { AssessmentService } from '../services/assessmentService';

const { FiBarChart3, FiUsers, FiTrendingUp, FiCalendar, FiRefreshCw, FiDownload } = FiIcons;

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState([]);
  const [recentAssessments, setRecentAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsResult, recentResult] = await Promise.all([
        AssessmentService.getAssessmentAnalytics(),
        AssessmentService.getRecentAssessments(20)
      ]);

      if (analyticsResult.success) {
        setAnalytics(analyticsResult.data);
      }

      if (recentResult.success) {
        setRecentAssessments(recentResult.data);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getOverallStats = () => {
    if (!analytics.length) return { total: 0, avgScore: 0, highNeed: 0, completionRate: 0 };

    const totals = analytics.reduce((acc, day) => ({
      total: acc.total + day.total_assessments,
      avgScore: acc.avgScore + (day.avg_percentage || 0),
      highNeed: acc.highNeed + day.high_need_count
    }), { total: 0, avgScore: 0, highNeed: 0 });

    return {
      total: totals.total,
      avgScore: analytics.length ? Math.round(totals.avgScore / analytics.length) : 0,
      highNeed: totals.highNeed,
      completionRate: 95 // Placeholder - would need actual tracking
    };
  };

  const getCategoryDistribution = () => {
    if (!analytics.length) return { high: 0, moderate: 0, selective: 0, minimal: 0 };

    return analytics.reduce((acc, day) => ({
      high: acc.high + day.high_need_count,
      moderate: acc.moderate + day.moderate_need_count,
      selective: acc.selective + day.selective_need_count,
      minimal: acc.minimal + day.minimal_need_count
    }), { high: 0, moderate: 0, selective: 0, minimal: 0 });
  };

  const stats = getOverallStats();
  const distribution = getCategoryDistribution();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <SafeIcon icon={FiRefreshCw} className="text-4xl text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Assessment Analytics</h1>
          <p className="text-text-secondary mt-2">Bridge Framework™ Assessment Dashboard</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="flex items-center px-4 py-2 border border-gray-1 rounded-lg text-text-secondary hover:bg-bg-light transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} className="mr-2" />
            Refresh
          </button>
          <button className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            <SafeIcon icon={FiDownload} className="mr-2" />
            Export Data
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-lg">
              <SafeIcon icon={FiUsers} className="text-2xl text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-text-secondary text-sm">Total Assessments</p>
              <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 bg-accent/10 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="text-2xl text-accent" />
            </div>
            <div className="ml-4">
              <p className="text-text-secondary text-sm">Average Score</p>
              <p className="text-2xl font-bold text-text-primary">{stats.avgScore}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <SafeIcon icon={FiBarChart3} className="text-2xl text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-text-secondary text-sm">High Need Cases</p>
              <p className="text-2xl font-bold text-text-primary">{stats.highNeed}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <div className="flex items-center">
            <div className="p-3 bg-sage-100 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-2xl text-sage-600" />
            </div>
            <div className="ml-4">
              <p className="text-text-secondary text-sm">Completion Rate</p>
              <p className="text-2xl font-bold text-text-primary">{stats.completionRate}%</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Category Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <h3 className="text-xl font-bold text-text-primary mb-6">Category Distribution</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
                <span className="text-text-secondary">High Need</span>
              </div>
              <span className="font-semibold text-text-primary">{distribution.high}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-accent rounded mr-3"></div>
                <span className="text-text-secondary">Moderate Need</span>
              </div>
              <span className="font-semibold text-text-primary">{distribution.moderate}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-primary-500 rounded mr-3"></div>
                <span className="text-text-secondary">Selective Need</span>
              </div>
              <span className="font-semibold text-text-primary">{distribution.selective}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-sage-500 rounded mr-3"></div>
                <span className="text-text-secondary">Minimal Need</span>
              </div>
              <span className="font-semibold text-text-primary">{distribution.minimal}</span>
            </div>
          </div>
        </motion.div>

        {/* Recent Assessments */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <h3 className="text-xl font-bold text-text-primary mb-6">Recent Assessments</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {recentAssessments.map((assessment, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-bg-light rounded-lg">
                <div>
                  <p className="font-medium text-text-primary text-sm">{assessment.user_name}</p>
                  <p className="text-text-secondary text-xs">{assessment.user_email}</p>
                </div>
                <div className="text-right">
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    assessment.percentage >= 75 ? 'bg-red-100 text-red-700' :
                    assessment.percentage >= 51 ? 'bg-accent/10 text-accent' :
                    assessment.percentage >= 25 ? 'bg-primary-100 text-primary-700' :
                    'bg-sage-100 text-sage-700'
                  }`}>
                    {assessment.percentage}%
                  </div>
                  <p className="text-text-light text-xs mt-1">
                    {new Date(assessment.completed_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Daily Analytics Chart */}
      {analytics.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-bg-primary rounded-xl p-6 ernest-shadow"
        >
          <h3 className="text-xl font-bold text-text-primary mb-6">Daily Assessment Trends</h3>
          <div className="overflow-x-auto">
            <div className="flex space-x-4 pb-4">
              {analytics.slice(0, 14).map((day, index) => (
                <div key={index} className="flex flex-col items-center min-w-0">
                  <div className="text-xs text-text-secondary mb-2">
                    {new Date(day.assessment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div 
                    className="w-8 bg-primary-500 rounded-t"
                    style={{ height: `${Math.max(day.total_assessments * 10, 10)}px` }}
                  ></div>
                  <div className="text-xs font-medium text-text-primary mt-2">
                    {day.total_assessments}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;